import { mergeUploadMetadata } from "./preserve-raster-image-metadata";
import { readPixelDimensions } from "./read-raster-upload-metadata";

const COMPRESSIBLE_IMAGE_MIME = /^image\/(jpeg|pjpeg|png|webp|bmp|x-ms-bmp)$/i;
// Formats whose canvas encoder honors the quality parameter. PNG/BMP do not (the
// quality argument is ignored for image/png), so they can only shrink by downscaling.
const LOSSY_IMAGE_MIME = /^image\/(jpeg|pjpeg|webp)$/i;

// The direct-to-S3 uploader's hard size cap (S3UploaderHelper::UPLOADED_IMAGE_MAX_SIZE,
// the forms' data-limit) is 100MB. Files OVER the cap are BLOCKED outright on the raw
// file before any preprocessing — no compression attempt (ART-8656); that gate lives in
// FilesUploader (rejectOversizeFiles, keyed off the configured maxFileSize) and runs
// before this function is ever called. Files at or under the cap upload pristine
// (byte-identical original, no re-encode — ART-8490/ART-8652); the only remaining
// recompression here is a pixel-count downscale for within-cap rasters that exceed the
// Sharp pixel budget.
// Output ceiling for the pixel-overflow downscale. 99 decimal MB reads under "100MB" in
// both decimal (Finder) and binary (Windows Explorer) units — a 99-MiB target showed
// up as "101.5 MB" to QA (ART-8652 round 2).
const COMPRESS_TARGET_BYTES = 99 * 1000 * 1000;
// The transform pipeline (Sharp) rejects inputs above its limitInputPixels default of
// 16383x16383 = 268,402,689 TOTAL pixels. A within-cap raster whose pixel count exceeds
// this is downscaled to fit — guard on the pixel count, not per-dimension, so a
// long-but-narrow original (e.g. 20000x8000 = 160MP, fine for Sharp) uploads untouched
// instead of being pointlessly re-encoded (ART-8652 round 2).
const MAX_UPLOAD_PIXELS = 16383 * 16383;

const canvasToBlobForUpload = (
  canvas: HTMLCanvasElement,
  type: string,
  quality?: number
): Promise<Blob> =>
  new Promise((resolve, reject) => {
    canvas.toBlob(
      (blob) => {
        if (blob && blob.size > 0) {
          resolve(blob);
        } else {
          // An oversized/failed canvas silently encodes to an empty or blank blob —
          // the old pipeline uploaded that garbage (ART-8652 round 2). Fail loudly.
          reject(new Error("canvas encode produced no data"));
        }
      },
      type,
      quality
    );
  });

const drawUploadImageAtScale = async (
  file: File,
  width: number,
  height: number,
  scale: number
): Promise<HTMLCanvasElement> => {
  const targetWidth = Math.max(1, Math.round(width * scale));
  const targetHeight = Math.max(1, Math.round(height * scale));

  // Downscale DURING decode where supported so a huge source never has to fit into
  // a full-size canvas (browsers cap canvas area around 16384x16384; beyond it the
  // draw silently produces a blank image). Engines without resize options decode
  // full-size and scale via drawImage; if that fails the error propagates and the
  // file falls back to Uppy's size gate instead of uploading blank.
  const bitmap = await createImageBitmap(file, {
    resizeWidth: targetWidth,
    resizeHeight: targetHeight,
    resizeQuality: "high",
  }).catch(() => createImageBitmap(file));

  const canvas = document.createElement("canvas");

  canvas.width = targetWidth;
  canvas.height = targetHeight;
  canvas.getContext("2d")?.drawImage(bitmap, 0, 0, targetWidth, targetHeight);
  bitmap.close();

  return canvas;
};

const uploadFileFromBlob = (
  originalFile: File,
  blob: Blob,
  type: string
): File => {
  const { name: originalName, lastModified } = originalFile;
  let name = originalName;

  if (type === "image/png" && /\.(bmp|dib)$/i.test(name)) {
    name = name.replace(/\.(bmp|dib)$/i, ".png");
  }

  return new File([blob], name, { type, lastModified });
};

const lossyUploadOutputType = (fileType: string): string =>
  /^image\/webp$/i.test(fileType) ? "image/webp" : "image/jpeg";

// Largest encoder quality whose output fits COMPRESS_TARGET_BYTES: try near-lossless
// first, then bisect. Deterministic, unlike browser-image-compression's fixed 0.95
// step-down loop, which overshot far below the target and, via its "must also end
// below the ORIGINAL size" clause, re-encoded every file it touched to just under
// its input size (ART-8652 round 2).
const findLargestFittingQuality = async (
  canvas: HTMLCanvasElement,
  type: string
): Promise<Blob | null> => {
  const nearLossless = await canvasToBlobForUpload(canvas, type, 0.99);

  if (nearLossless.size <= COMPRESS_TARGET_BYTES) {
    return nearLossless;
  }

  let low = 0.5;
  let high = 0.99;
  let best: Blob | null = null;

  for (let step = 0; step < 6; step++) {
    const quality = (low + high) / 2;
    // eslint-disable-next-line no-await-in-loop -- each probe depends on the previous bound
    const candidate = await canvasToBlobForUpload(canvas, type, quality);

    if (candidate.size <= COMPRESS_TARGET_BYTES) {
      best = candidate;
      low = quality;
    } else {
      high = quality;
    }
  }

  return best;
};

// Recompress an over-limit raster to the highest quality that fits the target.
// JPEG/WebP keep full resolution (downscaling only as a last resort when even
// quality 0.5 cannot fit). PNG/BMP have no quality knob, so they shrink by a
// downscale steered by the measured byte/pixel ratio (PNG bytes track pixels almost
// linearly) and stay PNG (alpha preserved) — landing just under the target rather
// than far below it. Throws when the file cannot be made to fit.
const recompressUploadToFit = async (
  file: File,
  width: number,
  height: number
): Promise<File> => {
  const pixelScale = Math.sqrt(MAX_UPLOAD_PIXELS / (width * height));
  const lossy = LOSSY_IMAGE_MIME.test(file.type);
  const outputType = lossy ? lossyUploadOutputType(file.type) : "image/png";
  const attempts = lossy ? 4 : 5;
  let scale = pixelScale >= 1 ? 1 : pixelScale * 0.999;

  for (let attempt = 0; attempt < attempts; attempt++) {
    // eslint-disable-next-line no-await-in-loop -- each attempt depends on the previous scale
    const canvas = await drawUploadImageAtScale(file, width, height, scale);

    if (lossy) {
      // eslint-disable-next-line no-await-in-loop -- each attempt depends on the previous scale
      const blob = await findLargestFittingQuality(canvas, outputType);

      if (blob) {
        return uploadFileFromBlob(file, blob, outputType);
      }

      scale *= 0.85;
    } else {
      // eslint-disable-next-line no-await-in-loop -- each attempt depends on the previous scale
      const encoded = await canvasToBlobForUpload(canvas, outputType);

      if (encoded.size <= COMPRESS_TARGET_BYTES) {
        return uploadFileFromBlob(file, encoded, outputType);
      }

      const guided =
        scale * Math.sqrt((COMPRESS_TARGET_BYTES * 0.95) / encoded.size);

      scale = Math.min(guided, scale * 0.95);
    }
  }

  throw new Error("could not compress the upload under the size limit");
};

// Over-cap files never reach here — FilesUploader.rejectOversizeFiles blocks them on the
// raw file before this runs. Within-pixel-budget files return unchanged (byte-identical
// pristine original — no canvas re-encode, which would strip the Photoshop XMP/IPTC
// "File Info" fields, ART-8415). A within-cap raster whose pixel count exceeds the Sharp
// budget is downscaled to fit; on failure the ORIGINAL file is returned so Uppy's
// maxFileSize restriction still rejects it with the standard size error.
export const compressRasterImageForUpload = async (
  file: File
): Promise<File> => {
  if (!file.type || !COMPRESSIBLE_IMAGE_MIME.test(file.type)) {
    return file;
  }

  const { width, height } = await readPixelDimensions(file);
  const withinPixelBudget =
    width > 0 && height > 0 && width * height <= MAX_UPLOAD_PIXELS;

  if (withinPixelBudget) {
    return file;
  }

  if (width === 0 || height === 0) {
    // Undecodable in this browser — leave it to the size restriction.
    return file;
  }

  let compressed: File;

  try {
    compressed = await recompressUploadToFit(file, width, height);
  } catch (error) {
    console.error("[compressRasterImageForUpload] compression failed", error);

    return file;
  }

  try {
    return await mergeUploadMetadata(file, compressed);
  } catch (error) {
    console.error(
      "[compressRasterImageForUpload] metadata merge failed",
      error
    );

    return compressed;
  }
};
